'use client'

/* eslint-disable @typescript-eslint/no-explicit-any*/

import { Download, Maximize, Minimize, X, ZoomIn, ZoomOut } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogOverlay, DialogPortal } from '@/components/ui/dialog'

import { DialogTitle } from './modal/dialog'

interface ImagePreviewModalProps {
  src: string
  alt: string
  isOpen: boolean
  onClose: () => void
  showDownload?: boolean
  className?: string
}

const ImagePreviewModal = React.forwardRef<React.ElementRef<typeof DialogContent>, ImagePreviewModalProps>(
  ({ src, alt, isOpen, onClose, showDownload = false, className }, ref) => {
    const [zoomLevel, setZoomLevel] = React.useState(1)
    const [position, setPosition] = React.useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = React.useState(false)
    const [isFullscreen, setIsFullscreen] = React.useState(false)
    const dragStartRef = React.useRef({ x: 0, y: 0 })
    const imgContainerRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
      if (!isOpen) {
        setZoomLevel(1)
        setPosition({ x: 0, y: 0 })
        setIsDragging(false)
        setIsFullscreen(false)
      }
    }, [isOpen])

    const handleClose = () => {
      setZoomLevel(1)
      setPosition({ x: 0, y: 0 })
      setIsDragging(false)
      setIsFullscreen(false)
      onClose()
    }

    const handleZoomIn = () => {
      setZoomLevel(prev => Math.min(prev + 0.2, 3)) // Max 300%
    }

    const handleZoomOut = () => {
      setZoomLevel(prev => Math.max(prev - 0.2, 0.5)) // Min 50%
    }

    const resetZoom = () => {
      setZoomLevel(1)
      setPosition({ x: 0, y: 0 })
    }

    const handleMouseDown = (e: React.MouseEvent) => {
      if (zoomLevel > 1) {
        setIsDragging(true)
        dragStartRef.current = { x: e.clientX - position.x, y: e.clientY - position.y }
      }
    }

    const handleMouseMove = (e: React.MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStartRef.current.x,
          y: e.clientY - dragStartRef.current.y,
        })
      }
    }

    const handleMouseUpOrLeave = () => {
      setIsDragging(false)
    }

    const toggleFullscreen = async () => {
      const el = imgContainerRef.current
      if (!el) return

      try {
        if (!isFullscreen) {
          if (el.requestFullscreen) {
            await el.requestFullscreen()
          } else if ((el as any).webkitRequestFullscreen) {
            await (el as any).webkitRequestFullscreen()
          } else {
            throw new Error('no Fullscreen API')
          }
        } else {
          if (document.exitFullscreen) {
            await document.exitFullscreen()
          } else if ((document as any).webkitExitFullscreen) {
            await (document as any).webkitExitFullscreen()
          }
        }
      } catch {
        if (!isFullscreen) {
          el.classList.add('pseudo-fullscreen')
          setIsFullscreen(true)
        } else {
          el.classList.remove('pseudo-fullscreen')
          setIsFullscreen(false)
        }
      }
    }

    React.useEffect(() => {
      const onFsChange = () => {
        const nativeFs = !!document.fullscreenElement || !!(document as any).webkitFullscreenElement
        setIsFullscreen(nativeFs)
        if (!nativeFs && imgContainerRef.current) {
          imgContainerRef.current.classList.remove('pseudo-fullscreen')
        }
      }
      document.addEventListener('fullscreenchange', onFsChange)
      document.addEventListener('webkitfullscreenchange', onFsChange)
      return () => {
        document.removeEventListener('fullscreenchange', onFsChange)
        document.removeEventListener('webkitfullscreenchange', onFsChange)
      }
    }, [])

    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          event.preventDefault()
          handleClose()
        }
      }

      if (isOpen) {
        document.addEventListener('keydown', handleKeyDown)
        // document.body.style.overflow = 'hidden'
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        // document.body.style.overflow = 'unset'
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    const handleDownload = () => {
      const link = document.createElement('a')
      link.href = src
      link.download = alt || 'image'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose()
      }
    }

    const handleContentClick = (e: React.MouseEvent) => {
      e.stopPropagation()
    }

    return (
      <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
        <DialogPortal>
          <DialogOverlay className="modal-overlay-detailed-image fixed inset-0 !z-[100000] bg-black/60 supports-[backdrop-filter]:bg-black/40 backdrop-blur-sm" />
          <DialogContent
            ref={ref}
            className={cn(
              'modal-content-detailed-image fixed left-[50%] top-[50%] w-[95vw] h-[95vh] max-w-none translate-x-[-50%] translate-y-[-50%] border-0 bg-transparent p-0 shadow-none focus:outline-none outline-none !z-[100000]',
              className
            )}
            onPointerDownOutside={e => {
              // Prevent the default behavior which would close the modal
              e.preventDefault()
              handleClose()
            }}
            onClick={handleContentClick}
          >
            <DialogTitle className="sr-only">Image Preview: {alt}</DialogTitle>
            {/* Header with controls */}
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              {showDownload && (
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={handleDownload}
                  className="bg-white/50 hover:bg-white/70 text-white border-0 rounded-full"
                  aria-label="Download image"
                >
                  <Download className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="secondary"
                size="icon"
                onClick={handleClose}
                className="bg-white/50 hover:bg-white/70 text-white border-0 rounded-full"
                aria-label="Close image preview"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Image container */}
            <div
              ref={imgContainerRef}
              className="relative w-full h-full flex items-center justify-center overflow-hidden cursor-pointer"
              onClick={handleOverlayClick}
            >
              <div
                className="relative select-none"
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
                  transformOrigin: 'center',
                  cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUpOrLeave}
                onMouseLeave={handleMouseUpOrLeave}
                onClick={handleContentClick}
              >
                <Image
                  src={src}
                  alt={alt}
                  width={1920}
                  height={1080}
                  className="max-w-[90vw] max-h-[80vh] w-auto h-auto object-contain pointer-events-none"
                  priority
                  unoptimized
                />
              </div>
            </div>

            {/* Bottom controls */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10" onClick={handleContentClick}>
              <div className="flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-lg p-2">
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.5}
                  className="bg-transparent hover:bg-white/20 text-white border-0 disabled:opacity-50"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>

                <Button
                  variant="secondary"
                  size="icon"
                  onClick={resetZoom}
                  disabled={zoomLevel === 1}
                  className="bg-transparent hover:bg-white/20 text-white border-0 disabled:opacity-50"
                >
                  <span className="text-xs font-medium">1:1</span>
                </Button>

                <Button
                  variant="secondary"
                  size="icon"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 3}
                  className="bg-transparent hover:bg-white/20 text-white border-0 disabled:opacity-50"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>

                <div className="h-4 w-px bg-white/30 mx-1" />

                <Button
                  variant="secondary"
                  size="icon"
                  onClick={toggleFullscreen}
                  className="bg-transparent hover:bg-white/20 text-white border-0"
                >
                  {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                </Button>

                <span className="text-xs text-white font-medium px-2">{Math.round(zoomLevel * 100)}%</span>
              </div>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    )
  }
)

ImagePreviewModal.displayName = 'ImagePreviewModal'

export { ImagePreviewModal }
